/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Just, Maybe } from 'purify-ts/Maybe';
import { LoggedInUser } from '@/models/user';
import { getDefaultGlobalPermissions } from '@/permissions';
import * as utils from '@/utils';
let disabledPersistance = false;
let toastMessage = null;
let localStorageError = false;
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var PersistanceStatus;
(function (PersistanceStatus) {
    PersistanceStatus["ENABLED"] = "ENABLED";
    PersistanceStatus["DISABLED"] = "DISABLED";
})(PersistanceStatus || (PersistanceStatus = {}));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function getPersistanceStatus() {
    return disabledPersistance ? PersistanceStatus.DISABLED : PersistanceStatus.ENABLED;
}
try {
    window.localStorage.setItem('vuex', '""');
    window.localStorage.removeItem('vuex');
    window.localStorage.setItem('@@', '1');
    if (window.localStorage.getItem('@@') !== '1') {
        throw new Error('Localstorage did not save state');
    }
    window.localStorage.removeItem('@@');
}
catch (e) {
    localStorageError = true;
    toastMessage = `Unable to persistently store user credentials, please check
        you browser privacy levels. You will not be logged-in in other tabs or
        when reloading.`;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const useLocalStorage = () => !disabledPersistance && !localStorageError;
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class LocalStorageValue {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(ns, key) {
        this.key = `CG_PERSIST-${ns}|${key}`;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get() {
        return Maybe.fromNullable(window.localStorage.getItem(this.key))
            .map(JSON.parse)
            .chainNullable(x => x);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clear() {
        window.localStorage.removeItem(this.key);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    set(value) {
        window.localStorage.setItem(this.key, value);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class AbstractPersistance {
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class SimplePersistance extends AbstractPersistance {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(ns, path) {
        super();
        this.ns = ns;
        this.path = path;
        this.storage = new LocalStorageValue(ns, path);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getValue(state) {
        return state[this.ns][this.path];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    retrieveValue() {
        return this.storage.get();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    storeValue(value) {
        this.storage.set(value);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    retrieveAndSetValue(state) {
        this.retrieveValue().ifJust(value => {
            state[this.ns] = {
                ...state[this.ns],
                [this.path]: value,
            };
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clear() {
        this.storage.clear();
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class UserPersitance extends AbstractPersistance {
    constructor() {
        super(...arguments);
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this.simpleJwt = new LocalStorageValue('user', 'jwtToken');
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this.simpleID = new LocalStorageValue('user', 'id');
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this.key = 'CG_PERSIST-user|user';
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    getValue(state) {
        return state.user.loggedInUser.mapOrDefault(user => ({
            id: user.id,
            tenantId: user.tenantId.extractNullable(),
            jwtToken: user.jwtToken,
        }), null);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clearOld() {
        this.simpleID.clear();
        this.simpleJwt.clear();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    storeValue(value) {
        try {
            this.clearOld();
        }
        catch (_) {
            // PASS
        }
        window.localStorage.setItem(this.key, value);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clear() {
        this.clearOld();
        window.localStorage.removeItem(this.key);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    retrieveAndSetValue(state) {
        const retrievedValue = Maybe.fromNullable(window.localStorage.getItem(this.key)).map(JSON.parse);
        retrievedValue
            .chainNullable(x => x)
            .caseOf({
            Just: x => Just(x),
            Nothing: () => this.simpleID
                .get()
                .chain(id => this.simpleJwt.get().map(jwt => ({
                id,
                tenantId: null,
                jwtToken: jwt,
            })))
                .ifJust(() => this.clearOld()),
        })
            .ifJust(user => {
            const loggedInUser = new LoggedInUser({
                id: user.id,
                isTestStudent: false,
                name: '',
                tenantId: Maybe.fromNullable(user.tenantId).filter(tId => tId !== ''),
                username: '',
            }, {
                jwtToken: user.jwtToken,
                canSeeHidden: false,
                permissions: getDefaultGlobalPermissions(),
                email: '',
                payments: [],
                usedCoupons: [],
            });
            state.user = {
                ...state.user,
                loggedInUser: Just(loggedInUser),
            };
        });
    }
}
const valuesToPersist = [
    new SimplePersistance('pref', 'fontSize'),
    new SimplePersistance('pref', 'contextAmount'),
    new SimplePersistance('ui_prefs', 'darkMode'),
    new UserPersitance(),
];
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const enablePersistance = (store) => {
    valuesToPersist.forEach(toPersist => {
        store.watch(state => toPersist.getValue(state), value => {
            if (useLocalStorage()) {
                toPersist.storeValue(JSON.stringify(value));
            }
        });
    });
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function disablePersistance() {
    let error;
    if (!useLocalStorage()) {
        disabledPersistance = true;
        return;
    }
    valuesToPersist.forEach(persist => {
        // Even on an error we try to clear all the remaining keys.
        try {
            persist.clear();
        }
        catch (e) {
            error = utils.coerceToError(e);
        }
    });
    if (error != null) {
        throw error;
    }
    disabledPersistance = true;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function onVueCreated($root) {
    // Do this in a timeout so that when we disable persistence when launching
    // in LTI we do not show this warning message as it makes no sense.
    setTimeout(() => {
        if (!disabledPersistance && toastMessage != null) {
            $root.$bvToast.toast(toastMessage, {
                title: 'Warning',
                variant: 'warning',
                toaster: 'b-toaster-top-right',
                noAutoHide: true,
                solid: true,
            });
        }
    }, 1000);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function onStoreCreated(store) {
    if (useLocalStorage()) {
        const newState = valuesToPersist.reduce((acc, persist) => {
            persist.retrieveAndSetValue(acc);
            return acc;
        }, { ...store.state });
        store.replaceState(newState);
        enablePersistance(store);
    }
}
